// Business API
import { apiRequest } from './request.js';
import { withCache, CACHE_STORE, getAuthToken } from './config.js';

export const businessAPI = {
    // Register business (complete onboarding)
    // F3: el wizard pasa un Idempotency-Key (UUID estable hasta success) para
    // que un retry tras 500 no duplique la empresa creada.
    registerComplete: async (businessData, { idempotencyKey } = {}) => {
        const body = businessData instanceof FormData ? businessData : JSON.stringify(businessData);
        const response = await apiRequest('/businesses/register-complete/', {
            method: 'POST',
            body: body,
            requireAuth: true,
            ...(idempotencyKey && { extraHeaders: { 'Idempotency-Key': idempotencyKey } })
        });

        // Store tokens and user data on successful registration
        if (response.token || response.access) {
            localStorage.setItem('authToken', response.token || response.access);
            window.dispatchEvent(new CustomEvent('auth-token-change'));
            if (response.refresh) {
                localStorage.setItem('refreshToken', response.refresh);
            }
            localStorage.setItem('currentUser', JSON.stringify(response.user));

            // Invalidate featured businesses to ensure the new one might appear if applicable
            CACHE_STORE.delete('featured_businesses');
        }

        return response;
    },

    // Crear un Business "draft" con datos mínimos (name + tax_id + business_type).
    // Lo usa el wizard al volver del modal de auth en Step 1: así el Business
    // existe en backend desde el primer paso y aparece en /dashboard/business
    // y en /admin/businesses como pendiente con registration_step=1, en vez de
    // perderse en localStorage si el user abandona antes del Preview.
    create: async (businessData) => {
        const response = await apiRequest('/businesses/', {
            method: 'POST',
            body: JSON.stringify(businessData),
            requireAuth: true,
        });
        CACHE_STORE.delete('featured_businesses');
        return response;
    },

    // Get all businesses (public)
    getAll: async (filters = {}) => {
        const queryParams = new URLSearchParams(filters).toString();
        const endpoint = queryParams ? `/businesses/?${queryParams}` : '/businesses/';

        return apiRequest(endpoint, {
            requireAuth: false
        });
    },

    // Get single business details (public) - with caching
    // Sends auth token if available to allow owner bypass in backend
    getById: async (businessId, forceRefresh = false) => {
        const cacheKey = `business_detail_${businessId}`;

        if (!forceRefresh) {
            const cached = CACHE_STORE.get(cacheKey);
            if (cached && (Date.now() - cached.timestamp < 5 * 60 * 1000)) {
                return cached.data;
            }
        }

        // Force refresh or expired cache
        CACHE_STORE.delete(cacheKey);
        // requireAuth: false → no fuerza refresh en 401 (evita que una página pública
        // expulse al usuario si su token está expirado).
        // Sin embargo, si hay token disponible lo enviamos manualmente en headers para
        // que el backend pueda identificar al dueño y mostrar su empresa aunque esté
        // en status=pending.
        const token = getAuthToken();
        const data = await apiRequest(`/businesses/${businessId}/`, {
            requireAuth: false,
            ...(token && { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } })
        });
        CACHE_STORE.set(cacheKey, { data, timestamp: Date.now() });
        return data;
    },

    // Get featured businesses (public) — no cache, must stay fresh
    // Backend uses two different fields: /featured/ endpoint vs is_featured filter — merge both
    getFeatured: async () => {
        const normalize = (data) => {
            if (Array.isArray(data)) return data;
            if (data && Array.isArray(data.results)) return data.results;
            return [];
        };

        const [a, b] = await Promise.allSettled([
            apiRequest('/businesses/featured/', { requireAuth: false }),
            apiRequest('/businesses/?is_featured=true&page_size=20', { requireAuth: false }),
        ]);

        const listA = a.status === 'fulfilled' ? normalize(a.value) : [];
        const listB = b.status === 'fulfilled' ? normalize(b.value) : [];

        // Merge and deduplicate by id
        const seen = new Set();
        return [...listA, ...listB].filter(b => {
            if (seen.has(b.id)) return false;
            seen.add(b.id);
            return true;
        });
    },

    // Discovery sections — top industrias + países dinámicos con sample
    // empresas precargadas. Backend cachea 1h. Usado por DiscoverySection
    // para llenar /advanced-search-results sin depender del historial
    // del usuario.
    getDiscoverySections: async () => {
        return apiRequest('/businesses/discovery-sections/', {
            requireAuth: false,
        });
    },

    // Admin-only: setea o limpia el override manual del sello de
    // verificación. `override` puede ser 'identity', 'premium' o null
    // (limpiar). `note` es la justificación opcional del admin.
    setVerificationOverride: async (businessId, { override, note = '' }) => {
        return apiRequest(`/businesses/${businessId}/verification-override/`, {
            method: 'POST',
            body: JSON.stringify({ override, note }),
        });
    },

    // Get unique coverage regions (public)
    getCoverage: async () => {
        return withCache('coverage', () => apiRequest('/businesses/coverage/', {
            requireAuth: false
        }));
    },

    // Get unique brands (public)
    getUniqueBrands: async () => {
        return withCache('unique_brands', () => apiRequest('/businesses/unique_brands/', {
            requireAuth: false
        }));
    },

    // Get unique countries (public)
    getUniqueCountries: async () => {
        return withCache('unique_countries', () => apiRequest('/businesses/unique_countries/', {
            requireAuth: false
        }));
    },

    // Buscar empresas existentes que matchean nombre/email/RUT antes de registrar.
    // Usado por el wizard de business-registration para alertar al user que la
    // empresa ya existe (preclaimed admin, ya es owner, o tiene asociación) en
    // vez de dejarlo crear un duplicado.
    matchExisting: async ({ name, email, tax_id } = {}) => {
        const params = new URLSearchParams();
        if (name) params.set('name', name);
        if (email) params.set('email', email);
        if (tax_id) params.set('tax_id', tax_id);
        const qs = params.toString();
        if (!qs) return { matches: [] };
        return apiRequest(`/businesses/match-existing/?${qs}`, { requireAuth: true });
    },

    // UX3 + Item 5: sugiere categorias (sector raiz + sub-vertical) basadas
    // en nombre + descripcion. Lo consume el SmartCategoryPicker del wizard.
    // Devuelve [{category_id, name, slug, confidence, reason}].
    suggestCategories: async ({ name = '', description = '' } = {}) => {
        return apiRequest('/businesses/suggest_categories/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, description }),
            requireAuth: false,
        });
    },

    // Update business (authenticated)
    update: async (businessId, updateData) => {
        const body = updateData instanceof FormData ? updateData : JSON.stringify(updateData);
        const response = await apiRequest(`/businesses/${businessId}/`, {
            method: 'PATCH',
            body: body
        });

        // Invalidate cache for this business
        CACHE_STORE.delete(`business_detail_${businessId}`);

        return response;
    },

    // Verify business ownership to activate enterprise billing (PK-73 / PK-83)
    // POST /api/business/verify-ownership/ — auth required, only the Business.owner.
    //   email_domain → 200 { owner_verified:true, owner_verified_at }
    //   document     → 202 { owner_verified:false, pending_review:true, request_id }
    // Errors thrown by apiRequest with err.status: 400 (missing fields / domain
    // mismatch), 403 (not owner), 404 (not found). 200 if already verified.
    verifyOwnership: async (businessId, verificationMethod, proof) => {
        const response = await apiRequest('/business/verify-ownership/', {
            method: 'POST',
            body: JSON.stringify({
                business_id: businessId,
                verification_method: verificationMethod,
                proof,
            }),
            requireAuth: true,
        });

        // Refresh cached business detail so owner_verified is re-read.
        CACHE_STORE.delete(`business_detail_${businessId}`);

        return response;
    },

    // Delete business (authenticated)
    delete: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/`, {
            method: 'DELETE'
        });
    },

    // Get business addresses (public)
    getBusinessAddresses: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/addresses/`, {
            requireAuth: false
        });
    },

    // Get unique certification names (public)
    getCertificationNames: async () => {
        return withCache('certification_names', () => apiRequest('/businesses/certifications/unique-names/', {
            requireAuth: false
        }));
    },

    // Business Services (authenticated)
    getServices: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/services/`, { requireAuth: false });
    },
    createService: async (businessId, data) => {
        return apiRequest(`/businesses/${businessId}/services/`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },
    updateService: async (businessId, serviceId, data) => {
        return apiRequest(`/businesses/${businessId}/services/${serviceId}/`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    },
    deleteService: async (businessId, serviceId) => {
        return apiRequest(`/businesses/${businessId}/services/${serviceId}/`, {
            method: 'DELETE'
        });
    },

    // Business Products (authenticated)
    getProducts: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/products/`, { requireAuth: false });
    },
    createProduct: async (businessId, data) => {
        return apiRequest(`/businesses/${businessId}/products/`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },
    updateProduct: async (businessId, productId, data) => {
        return apiRequest(`/businesses/${businessId}/products/${productId}/`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    },
    deleteProduct: async (businessId, productId) => {
        return apiRequest(`/businesses/${businessId}/products/${productId}/`, {
            method: 'DELETE'
        });
    },

    // Fase 7 del rediseño: deshacer subida masiva (productos + servicios del batch)
    deleteBulkBatch: async (businessId, batchId) => {
        return apiRequest(`/businesses/${businessId}/bulk-batch/${batchId}/`, {
            method: 'DELETE'
        });
    },

    // Business analytics and contacts (authenticated owner)
    getAnalytics: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/analytics/`);
    },

    // DEPRECATED: Use leadsAPI.getAll({ business: businessId }) instead
    // This endpoint doesn't exist in API_DOC.md
    getBusinessLeads: async (businessId) => {
        console.warn('businessAPI.getBusinessLeads is deprecated. Use leadsAPI.getAll({ business: businessId }) instead.');
        // Redirect to correct endpoint
        return apiRequest(`/leads/?business=${businessId}`);
    },

    // Business Photos (authenticated)
    getPhotos: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/photos/`, { requireAuth: false });
    },
    addPhoto: async (businessId, url) => {
        return apiRequest(`/businesses/${businessId}/add_photo/`, {
            method: 'POST',
            body: JSON.stringify({ url })
        });
    },
    reorderPhotos: async (businessId, order) => {
        return apiRequest(`/businesses/${businessId}/photos/order/`, {
            method: 'PATCH',
            body: JSON.stringify({ order })
        });
    },
    deletePhoto: async (businessId, photoId) => {
        return apiRequest(`/businesses/${businessId}/photos/${photoId}/`, {
            method: 'DELETE'
        });
    },

    // Product Photos (authenticated)
    getProductPhotos: async (businessId, productId) => {
        return apiRequest(`/businesses/${businessId}/products/${productId}/photos/`, { requireAuth: false });
    },
    addProductPhoto: async (businessId, productId, url) => {
        return apiRequest(`/businesses/${businessId}/products/${productId}/photos/`, {
            method: 'POST',
            body: JSON.stringify({ url })
        });
    },
    reorderProductPhotos: async (businessId, productId, order) => {
        return apiRequest(`/businesses/${businessId}/products/${productId}/photos/order/`, {
            method: 'PATCH',
            body: JSON.stringify({ order })
        });
    },
    deleteProductPhoto: async (businessId, productId, photoId) => {
        return apiRequest(`/businesses/${businessId}/products/${productId}/photos/${photoId}/`, {
            method: 'DELETE'
        });
    },

    // Legal Documents (authenticated)
    getLegalDocuments: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/legal_documents/`, { requireAuth: false });
    },
    addLegalDocument: async (businessId, formData) => {
        return apiRequest(`/businesses/${businessId}/add_legal_document/`, {
            method: 'POST',
            body: formData
        });
    },
    deleteLegalDocument: async (businessId, docId) => {
        return apiRequest(`/businesses/${businessId}/legal_documents/${docId}/`, {
            method: 'DELETE'
        });
    },

    // Get businesses owned by the authenticated user
    getMyBusinesses: async () => {
        return apiRequest('/businesses/my_businesses/', {
            requireAuth: true
        });
    },

    // Business Admins Management
    getAdmins: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/admins/`);
    },

    addAdmin: async (businessId, adminData) => {
        return apiRequest(`/businesses/${businessId}/add_admin/`, {
            method: 'POST',
            body: JSON.stringify(adminData)
        });
    },

    updateAdmin: async (businessId, adminId, permissions) => {
        return apiRequest(`/businesses/${businessId}/admins/${adminId}/update/`, {
            method: 'PATCH',
            body: JSON.stringify(permissions)
        });
    },

    removeAdmin: async (businessId, adminId) => {
        return apiRequest(`/businesses/${businessId}/admins/${adminId}/`, {
            method: 'DELETE'
        });
    },

    getAdminLogs: async (businessId, adminId) => {
        return apiRequest(`/businesses/${businessId}/admins/${adminId}/logs/`);
    },

    // ── Asociación Profesional ──────────────────────────────────────────────

    // Solicitar asociarse a un Business Profile (crea registro pending).
    // corporateEmail: correo corporativo del solicitante, obligatorio desde el front.
    requestAssociation: async (businessId, message = '', corporateEmail = '') => {
        return apiRequest(`/businesses/${businessId}/request-association/`, {
            method: 'POST',
            body: JSON.stringify({ request_message: message, corporate_email: corporateEmail }),
            requireAuth: true
        });
    },

    // Crear empresa preclaimed (no existe en directorio) y solicitar asociarse en un solo paso.
    // Estilo LinkedIn: si la empresa no aparece, el usuario la propone y queda pendiente
    // de revisión por el admin de ProveMiner.
    createAndRequestAssociation: async (name, message = '', corporateEmail = '') => {
        return apiRequest(`/businesses/create-and-request-association/`, {
            method: 'POST',
            body: JSON.stringify({ name, request_message: message, corporate_email: corporateEmail }),
            requireAuth: true
        });
    },

    // Búsqueda específica para el flujo de asociación. Incluye empresas
    // aprobadas+visibles del directorio Y empresas preclaimed en revisión
    // (creadas por otro profesional), para evitar duplicados.
    searchForAssociation: async (query) => {
        const q = encodeURIComponent((query || '').trim());
        return apiRequest(`/businesses/search-for-association/?q=${q}`, {
            method: 'GET',
            requireAuth: true
        });
    },

    // Confirmar correo corporativo con el token recibido por mail.
    // Endpoint público (no requiere auth) — el token ES la auth.
    confirmCorporateEmail: async (token) => {
        return apiRequest(`/businesses/confirm-corporate-email/`, {
            method: 'POST',
            body: JSON.stringify({ token }),
            requireAuth: false
        });
    },

    // Verificación de correo corporativo. Dos modos según businessId:
    //   - sin businessId: PROFILE-ONLY (solo badge en perfil, sin asociación).
    //   - con businessId: JOIN — crea BusinessAdmins colaborador active y
    //     manda mail de verificación. No requiere aprobación del owner; el
    //     correo verificado es el control.
    verifyPersonalCorporateEmail: async (corporateEmail, companyName, businessId = null) => {
        const body = { corporate_email: corporateEmail, company_name: companyName };
        if (businessId) body.business_id = businessId;
        return apiRequest(`/profiles/verify-corporate-email/`, {
            method: 'POST',
            body: JSON.stringify(body),
            requireAuth: true
        });
    },

    // Pide cambio de rol sobre una asociación activa. Manda el rol nuevo y
    // un mensaje opcional. Si la empresa tiene plan con has_team_admin_management
    // (default), lo aprueba el owner; si no, el platform admin.
    requestRoleChange: async (associationId, newRole, requestMessage = '') => {
        return apiRequest(`/businesses/associations/${associationId}/request-role-change/`, {
            method: 'POST',
            body: JSON.stringify({ new_role: newRole, request_message: requestMessage }),
            requireAuth: true
        });
    },

    cancelRoleChange: async (associationId) => {
        return apiRequest(`/businesses/associations/${associationId}/cancel-role-change/`, {
            method: 'DELETE',
            requireAuth: true
        });
    },

    // Downgrade: el user se autoasigna un rol de igual o menor poder. Se
    // aplica inmediato y notifica al owner por la campana.
    downgradeRole: async (associationId, newRole) => {
        return apiRequest(`/businesses/associations/${associationId}/downgrade-role/`, {
            method: 'POST',
            body: JSON.stringify({ new_role: newRole }),
            requireAuth: true
        });
    },

    ownerApproveRoleChange: async (businessId, associationId) => {
        return apiRequest(`/businesses/${businessId}/admins/${associationId}/approve-role-change/`, {
            method: 'PATCH',
            requireAuth: true
        });
    },

    adminApproveRoleChange: async (associationId) => {
        return apiRequest(`/admin/association-requests/${associationId}/approve-role-change/`, {
            method: 'PATCH',
            requireAuth: true
        });
    },

    // Reenviar el correo de verificación para una asociación específica del
    // usuario actual. Invalida tokens previos.
    resendCorporateEmailVerification: async (associationId) => {
        return apiRequest(`/businesses/associations/${associationId}/resend-corporate-email-verification/`, {
            method: 'POST',
            requireAuth: true
        });
    },

    // Edita el cargo personal del usuario en una empresa (BusinessAdmins.job_title).
    // Texto libre, no son permisos.
    updateAssociationJobTitle: async (associationId, jobTitle) => {
        return apiRequest(`/businesses/associations/${associationId}/update-job-title/`, {
            method: 'PATCH',
            body: JSON.stringify({ job_title: jobTitle }),
            requireAuth: true
        });
    },

    // Cambia el correo corporativo declarado en una asociación del usuario.
    // Resetea corporate_email_verified, invalida tokens previos y reenvía mail
    // al nuevo destinatario.
    updateAssociationCorporateEmail: async (associationId, corporateEmail) => {
        return apiRequest(`/businesses/associations/${associationId}/update-corporate-email/`, {
            method: 'PATCH',
            body: JSON.stringify({ corporate_email: corporateEmail }),
            requireAuth: true
        });
    },

    // Edita la nota libre de una asociación. Preserva el prefijo de rol —
    // no cambia rol ni permisos.
    updateAssociationNote: async (associationId, note) => {
        return apiRequest(`/businesses/associations/${associationId}/update-note/`, {
            method: 'PATCH',
            body: JSON.stringify({ note }),
            requireAuth: true
        });
    },

    // Owner endpoints (modelo Google Workspace): el dueño del Business
    // aprueba/rechaza miembros pendientes de SU empresa directamente sin
    // pasar por el platform admin.
    getPendingAdmins: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/admins/pending/`);
    },

    approveAdminAsOwner: async (businessId, associationId, permissions = {}) => {
        const body = {
            can_edit_products: !!permissions.can_edit_products,
            can_edit_services: !!permissions.can_edit_services,
            can_manage_reviews: !!permissions.can_manage_reviews,
            can_view_analytics: !!permissions.can_view_analytics,
        };
        return apiRequest(`/businesses/${businessId}/admins/${associationId}/approve/`, {
            method: 'PATCH',
            body: JSON.stringify(body),
            requireAuth: true,
        });
    },

    rejectAdminAsOwner: async (businessId, associationId, rejectionReason) => {
        return apiRequest(`/businesses/${businessId}/admins/${associationId}/reject/`, {
            method: 'PATCH',
            body: JSON.stringify({ rejection_reason: rejectionReason }),
            requireAuth: true,
        });
    },

    // Dejar una asociación activa (pasa a revoked)
    leaveAssociation: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/leave-association/`, {
            method: 'DELETE',
            requireAuth: true
        });
    },

    // Ver mis solicitudes de asociación (?status=pending|active|rejected|revoked)
    getMyAssociationRequests: async (statusFilter = null) => {
        const params = statusFilter ? `?status=${statusFilter}` : '';
        return apiRequest(`/me/association-requests/${params}`, { requireAuth: true });
    },

    // Negocios sugeridos por LinkedIn headline del usuario
    getLinkedInMatches: async () => {
        return apiRequest('/businesses/linkedin-match/', { requireAuth: true });
    },

    // ── Mensajes de revisión admin → owner ──
    // Devuelve { unread, results } donde results es la lista de
    // BusinessReviewMessage (acción del admin, mensaje, CTA sugerida).
    getReviewMessages: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/review-messages/`, {
            requireAuth: true,
        });
    },

    // Marca todos los mensajes como leídos (cuando el owner abre el panel).
    markReviewMessagesRead: async (businessId) => {
        return apiRequest(`/businesses/${businessId}/review-messages/`, {
            method: 'POST',
            requireAuth: true,
        });
    },

    // Marca un mensaje individual como resuelto (cuando el owner ya
    // actuó sobre la observación, p. ej. subió un nuevo doc).
    resolveReviewMessage: async (businessId, messageId) => {
        return apiRequest(`/businesses/${businessId}/review-messages/${messageId}/resolve/`, {
            method: 'POST',
            requireAuth: true,
        });
    },

    // Owner pide republicar empresa despublicada por el equipo. Crea un
    // mensaje al admin con motivo opcional + marca el flag para que
    // aparezca en la bandeja admin como item accionable.
    requestRepublish: async (businessId, message = '') => {
        return apiRequest(`/businesses/${businessId}/republish-request/`, {
            method: 'POST',
            body: JSON.stringify({ message }),
            requireAuth: true,
        });
    },
};
